import { useMemo } from 'react'

const sortCoins = (coinList = []) =>
    coinList.sort(
        (coin, nextCoin) => coin.isCrypto - coin.isCrypto || coin.name.localeCompare(nextCoin.name),
    )

const isCoinFilterOut = ({ searchTermLowerCased, coin }) =>
    searchTermLowerCased &&
    !coin.name.toLowerCase().includes(searchTermLowerCased) &&
    !coin.code.toLowerCase().includes(searchTermLowerCased)

const isCoinAvailableWithOppositeInPair = ({
    coin,
    availableOppositeCoinPairs,
    allAvailablePairs = [],
}) => {
    if (availableOppositeCoinPairs[coin.code]) {
        return true
    }

    // Check if coin appears in any pair as either from or to currency
    return allAvailablePairs.some(pair => {
        const fromCode = pair.fromCurrencyDto?.code
        const toCode = pair.toCurrencyDto?.code

        if (fromCode === coin.code) {
            return availableOppositeCoinPairs[toCode]
        }

        if (toCode === coin.code) {
            return availableOppositeCoinPairs[fromCode]
        }
        return false
    })
}

const getAggregatedCoinLists = ({
    availableOppositeCoinPairs,
    coinList,
    searchTerm = '',
    allAvailablePairs = [],
}) => {
    const searchTermLowerCased = searchTerm.toLowerCase()

    const result = coinList.reduce(
        (coinListsDto, coin) => {
            if (isCoinFilterOut({ searchTermLowerCased, coin })) {
                return coinListsDto
            }

            if (
                isCoinAvailableWithOppositeInPair({
                    coin,
                    availableOppositeCoinPairs,
                    allAvailablePairs,
                })
            ) {
                coinListsDto.availableCoins.push(coin)
            } else {
                coinListsDto.unavailableCoins.push(coin)
            }

            return coinListsDto
        },
        { availableCoins: [], unavailableCoins: [] },
    )

    return result
}

const getCoinLists = ({
    availableOppositeCoinPairs,
    coinList,
    searchTerm = '',
    allAvailablePairs = [],
}) => {
    const { availableCoins, unavailableCoins } = getAggregatedCoinLists({
        availableOppositeCoinPairs,
        coinList,
        searchTerm,
        allAvailablePairs,
    })

    return {
        availableCoins: sortCoins(availableCoins),
        unavailableCoins: sortCoins(unavailableCoins),
    }
}

const useGetCoinLists = ({
    availableOppositeCoinPairs,
    coinList,
    searchTerm = '',
    allAvailablePairs = [],
}) => {
    const { availableCoins, unavailableCoins } = useMemo(
        () =>
            getCoinLists({
                availableOppositeCoinPairs,
                coinList,
                searchTerm,
                allAvailablePairs,
            }),
        [availableOppositeCoinPairs, coinList, searchTerm, allAvailablePairs],
    )

    return {
        availableCoins,
        unavailableCoins,
    }
}

export { useGetCoinLists }
